import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Keycloak from "keycloak-js";
import { getActiveTenantSlug } from "./tenantContext";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
});

let keycloakInstance: Keycloak | null = null;

export function setKeycloakInstance(kc: Keycloak): void {
  keycloakInstance = kc;
}

// Request interceptor: attach Keycloak bearer token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (keycloakInstance?.authenticated && keycloakInstance.token) {
      // Refresh token if it expires within 30 seconds
      try {
        await keycloakInstance.updateToken(30);
      } catch {
        keycloakInstance.login();
        return Promise.reject(new Error("Token refresh failed"));
      }
      config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    }
    // Tenant picked in the selector — what tenant-wide calls (listings,
    // provisioning, billing) act on. The backend honours it for admins
    // only, and overrides it with the owner's tenant for calls aimed at
    // one resource.
    const tenantSlug = getActiveTenantSlug();
    if (tenantSlug) {
      config.headers["X-Tenant-Slug"] = tenantSlug;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string }>) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        console.warn("API returned 401 Unauthorized");
      }

      if (status === 403) {
        console.error("Forbidden: insufficient permissions");
      }

      // Attach a user-friendly message
      const message =
        data?.detail || error.message || "An unexpected error occurred";
      return Promise.reject(new Error(message));
    }

    if (error.request) {
      return Promise.reject(
        new Error("Network error: unable to reach the server")
      );
    }

    return Promise.reject(error);
  }
);

export default apiClient;
